import { useState } from "react";
import { submitLead, apiErr } from "@/lib/api";
import { SMS_CONSENT } from "@/data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export function LeadForm({ type, source, title, successMsg, extraFields = [], defaults = {}, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", contact_preference: "Email", ...defaults });
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name || !form.email) { setErr("Please provide your name and email."); return; }
    setLoading(true);
    const details = {};
    extraFields.forEach((f) => { if (form[f.name] !== undefined) details[f.label] = form[f.name]; });
    try {
      await submitLead({
        lead_type: type, name: form.name, email: form.email, phone: form.phone || "",
        source_page: source || "", county: form.county || defaults.county || "", city: form.city || "",
        price_range: form.price_range || "", timeline: form.timeline || "",
        preapproved: form.preapproved || "", contact_preference: form.contact_preference,
        sms_consent: consent, details,
      });
      setDone(true);
      onDone && onDone();
    } catch (e2) {
      setErr(apiErr(e2.response?.data?.detail) || "Submission failed. Please try again.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="lead-success">
        <CheckCircle2 className="mx-auto text-green mb-4" size={44} />
        <h3 className="font-serif text-2xl text-navy mb-2">Request Received</h3>
        <p className="text-charcoal/70 max-w-sm mx-auto">{successMsg || "Thank you. Richard will follow up with you personally."}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
      {title && <h3 className="font-serif text-2xl text-navy">{title}</h3>}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Full name *</Label>
          <Input value={form.name} onChange={set("name")} data-testid="lead-name" placeholder="Jane Doe" />
        </div>
        <div>
          <Label>Phone</Label>
          <Input value={form.phone} onChange={set("phone")} data-testid="lead-phone" placeholder="(404) 000-0000" />
        </div>
      </div>
      <div>
        <Label>Email *</Label>
        <Input type="email" value={form.email} onChange={set("email")} data-testid="lead-email" placeholder="you@email.com" />
      </div>
      {extraFields.map((f) => (
        <div key={f.name}>
          <Label>{f.label}</Label>
          {f.type === "textarea"
            ? <Textarea value={form[f.name] || ""} onChange={set(f.name)} data-testid={`lead-${f.name}`} placeholder={f.placeholder} />
            : f.type === "select"
              ? <select value={form[f.name] || ""} onChange={set(f.name)} data-testid={`lead-${f.name}`} className="w-full border border-input rounded-md h-10 px-3 bg-white text-sm">
                  <option value="">Select…</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              : <Input value={form[f.name] || ""} onChange={set(f.name)} data-testid={`lead-${f.name}`} placeholder={f.placeholder} />}
        </div>
      ))}
      <div className="flex items-start gap-3 pt-2">
        <Checkbox id={`consent-${type}`} checked={consent} onCheckedChange={setConsent} data-testid="lead-consent" className="mt-1" />
        <label htmlFor={`consent-${type}`} className="text-xs text-charcoal/60 leading-relaxed">{SMS_CONSENT}</label>
      </div>
      {err && <p className="text-destructive text-sm" data-testid="lead-error">{err}</p>}
      <Button type="submit" disabled={loading} data-testid="lead-submit"
        className="w-full bg-gold text-navy font-semibold rounded-full h-12 hover:bg-gold/90">
        {loading ? "Sending…" : "Submit Request"}
      </Button>
    </form>
  );
}

export function LeadDialog({ trigger, ...props }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-cream max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">{props.title || "Get in Touch"}</DialogTitle></DialogHeader>
        <LeadForm {...props} title={null} onDone={() => props.onDone && props.onDone()} />
      </DialogContent>
    </Dialog>
  );
}
